import type { GpsGate } from '../contracts';

export interface GpsGateOptions {
  /** Number of recent accuracies whose median is used. */
  window: number;
  /** 'ok' -> 'degraded' after the median exceeded degradeFactor * threshold this long (ms). */
  degradeAfterMs: number;
  degradeFactor: number;
  /** No fix for this long (ms) counts as "bad" (and blocks 'ok'). */
  staleMs: number;
}

export const DEFAULT_GATE_OPTIONS: GpsGateOptions = {
  window: 3,
  degradeAfterMs: 5000,
  degradeFactor: 2,
  staleMs: 5000,
};

/**
 * GPS quality gate:
 *  waiting  -> ok        when the median of the last 3 accuracies <= threshold
 *  ok       -> degraded  when the median stays > 2 x threshold (or no fix arrives) for > 5 s
 *  degraded -> ok        when the median is <= threshold again
 */
export class GpsGateTracker {
  readonly opts: GpsGateOptions;
  state: GpsGate = 'waiting';
  private readonly acc: Float64Array;
  private readonly sorted: Float64Array;
  private count = 0;
  private head = 0;
  private lastFixTime = -Infinity;
  private badSince: number | null = null;

  constructor(opts: Partial<GpsGateOptions> = {}) {
    this.opts = { ...DEFAULT_GATE_OPTIONS, ...opts };
    this.acc = new Float64Array(this.opts.window);
    this.sorted = new Float64Array(this.opts.window);
  }

  reset(): void {
    this.state = 'waiting';
    this.count = 0;
    this.head = 0;
    this.lastFixTime = -Infinity;
    this.badSince = null;
  }

  addFix(accuracy: number, time: number): void {
    this.acc[this.head] = accuracy;
    this.head = (this.head + 1) % this.acc.length;
    if (this.count < this.acc.length) this.count++;
    if (time > this.lastFixTime) this.lastFixTime = time;
  }

  /** Median of the recent accuracies, NaN until `window` fixes were seen. */
  median(): number {
    const n = this.count;
    if (n < this.acc.length) return NaN;
    const s = this.sorted;
    for (let i = 0; i < n; i++) s[i] = this.acc[i];
    // insertion sort (n = 3)
    for (let i = 1; i < n; i++) {
      const v = s[i];
      let j = i - 1;
      while (j >= 0 && s[j] > v) {
        s[j + 1] = s[j];
        j--;
      }
      s[j + 1] = v;
    }
    return n % 2 === 1 ? s[(n - 1) >> 1] : 0.5 * (s[n / 2 - 1] + s[n / 2]);
  }

  update(now: number, thresholdM: number): GpsGate {
    const m = this.median();
    const stale = now - this.lastFixTime > this.opts.staleMs;
    const good = !stale && m <= thresholdM; // false for NaN
    switch (this.state) {
      case 'waiting':
        if (good) this.state = 'ok';
        this.badSince = null;
        break;
      case 'ok': {
        const bad = stale || m > this.opts.degradeFactor * thresholdM;
        if (!bad) {
          this.badSince = null;
        } else {
          if (this.badSince === null) this.badSince = now;
          if (now - this.badSince > this.opts.degradeAfterMs) this.state = 'degraded';
        }
        break;
      }
      case 'degraded':
        if (good) {
          this.state = 'ok';
          this.badSince = null;
        }
        break;
    }
    return this.state;
  }
}
